import {
    AnimationBaseController,
    IAnimationControllerConfiguration,
    Inject,
    ModelEvents,
} from '@denishussar/html5-game-engine';

import { GameModel } from '../../Models/GameModel';
import {
    IPaletteLisControllerOptions,
    PaletteListController,
    PaletteListControllerType,
} from './PaletteListController';
import { IPaletteListItemControllerOptions, PaletteListItemController } from './PaletteListItemController';
import { GameLogic } from '../../Logic/GameLogic';
import { Container, Text } from 'pixi.js';
import { GameEvent, GameEventSceneButtonClick } from '../../Constants/GameEvent';
import { IPaletteMonetizationPointsConfigurations, PaletteMonetizationPointsController } from './PaletteMonetizationPointsController';

export class PaletteControllerMobile extends AnimationBaseController {
    $model: GameModel;

    private _width: number = 0;
    private _height: number = 0;
    private _leftHeight: number = 0;

    private _listController: PaletteListController = null;
    private _paletteHand: PaletteListItemController = null;

    public constructor(
        @Inject private _options: any,
        @Inject private _gameLogic: GameLogic,
        @Inject private _modelEvents: ModelEvents,
        @Inject private _model: GameModel,
    ) {
        super('PaletteControllerMobile');
    }

    protected getConfiguration(): IAnimationControllerConfiguration {
        return <IAnimationControllerConfiguration>{
            childControllers: {
                monetizationPoints: {
                    controller: PaletteMonetizationPointsController,
                    model: this.$model,
                    options: <IPaletteMonetizationPointsConfigurations>{}
                },
                list: {
                    controller: PaletteListController,
                    model: this.$model,
                    options: <IPaletteLisControllerOptions>{
                        type: PaletteListControllerType.vertical,
                        onClick: (index: number) => {
                            this._gameLogic.setColor(index);
                        },
                    },
                },
            },
            modelEvents: {
                progressColor: (color: number, index: number, progress: number) => {
                    progress = this._gameLogic.getFillProgress();
                    this.updatePerventTextByProgress(progress);
                    this.updatePercentBarPositionByProgress(progress);
                },
            },
        };
    }

    protected onInitialize(): void {
        this._width = this.display.width;
        this._height = this.display.height;

        this._listController = this.$childControllers.get<PaletteListController>('list');
        this.display.setChildIndex(this._listController.display, 1);

        const progress = this._gameLogic.getFillProgress();
        this.updatePerventTextByProgress(progress);
        this.updatePercentBarPositionByProgress(progress);

        this._paletteHand = this.addChildController({
            controller: PaletteListItemController,
            model: this.$model,
            options: <IPaletteListItemControllerOptions>{
                color: -1,
                index: 0,
                progress: this._gameLogic.getFillProgress(-1),
                symbol: null,
                onClick: () => {
                    this._listController.selectItem(0);
                },
            },
        }, `item_palet_hand`) as PaletteListItemController;

        this._listController.items.unshift(this._paletteHand);

        this.$childControllers.get('monetizationPoints').visible = !this._gameLogic.isFTUE;
    }

    protected onResize(width: number, height: number, scale: number) {
        this.updatePercentBarPositionByProgress(this._gameLogic.getFillProgress());

        
        const progress = this.getDisplayObjectByQuery('progress')[0] as Container;
        (this.$childControllers.get('monetizationPoints') as PaletteMonetizationPointsController)
            .setBounds(progress.x, progress.y, width, height);
    }

    protected onUpdate(frameRate: number) {
        this._paletteHand.x = this._width / 2 - PaletteListItemController.width / 2 - 30;
        this._paletteHand.y = 85;

        this._listController.x = this._paletteHand.x;
        this._listController.y = this._paletteHand.y - 60;
    }

    public setBackgroundSize(value: number): void {
        this.display.getChildAt(0).height = value;

        const margin = 60;

        const borderBottom = this.getBorderElement('bottom');
        borderBottom.y = value - 40

        this._listController.setMaskBounds(
            -140,
            155,
            300,
            value - 220
        );

        const bgLeft = this.getProgressBarElement('bg_left');
        const bgMid = this.getProgressBarElement('bg_mid');
        const bgRight = this.getProgressBarElement('bg_right');

        const left = this.getProgressBarElement('left');
        this._leftHeight = left.height;

        const mid = this.getProgressBarElement('mid');
        const right = this.getProgressBarElement('right');

        const percent = this.getDisplayObjectByQuery('progress.percent')[0] as Container;
        const percentText = this.getDisplayObjectByQuery('progress.percent.txt')[0] as Text;

        const progressBgMargin = 40;

        bgLeft.y = progressBgMargin;
        bgMid.y = bgLeft.y + bgLeft.height;
        bgMid.height = value - bgLeft.height - bgRight.height - progressBgMargin * 2;
        bgRight.y = bgMid.y + bgMid.height;

        const progressMargin = progressBgMargin + 7;

        left.y = progressMargin;
        mid.y = left.y + this._leftHeight;
        mid.height = value - left.height - right.height - progressMargin * 2;
        right.y = mid.y + mid.height;

        percentText.anchor.set(0.5, 0);
        percentText.position.x = 0;

        this.updatePercentBarPositionByProgress(this._gameLogic.getFillProgress());
        this.updateMonetizationPoints(value);
    }

    private getProgressBarElement(name: string): Container {
        const container = this.getDisplayObjectByQuery(`progress.bar.${name}`)[0] as Container;
        return container;
    }

    private getBorderElement(name: string): Container {
        const container = this.getDisplayObjectByQuery(`border_${name}`)[0] as Container;
        return container;
    }

    private updatePerventTextByProgress(progress: number): void {
        const percentText = this.getDisplayObjectByQuery('progress.percent.txt')[0] as Text;
        percentText.text = Math.floor(progress * 100) + '%';
    }

    private updateMonetizationPoints(value: number) {
        const progress = this.getDisplayObjectByQuery('progress')[0] as Container;
        (this.$childControllers.get('monetizationPoints') as PaletteMonetizationPointsController)
            .setBounds(progress.x + progress.height / 2, progress.y, value - progress.height / 2, progress.height);
    }

    private updatePercentBarPositionByProgress(progress: number): void {
        const left = this.getProgressBarElement('left');
        const mid = this.getProgressBarElement('mid');
        const right = this.getProgressBarElement('right');
        const percent = this.getDisplayObjectByQuery('progress.percent')[0] as Container;
        const percentHeight = percent.height / 2;

        const length = right.y + right.height - left.y;
        const position = length * progress + percentHeight * (1 - progress);
        const progressLength = length * progress;
        percent.y = position;

        right.alpha = 0;
        if (progressLength <= mid.y) {
            mid.alpha = 0;
            left.height = progressLength;
        } else {
            mid.alpha = 1;
            mid.height = position - mid.y;
            left.height = mid.y;
        }
    }
}
